import { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ZoomIn, ZoomOut, Maximize2, Home } from 'lucide-react';

// Station represents a screen/component in the app
interface Station {
  id: string;
  name: string;
  component: string;
  x: number; // SVG coordinates
  y: number;
  lines: string[]; // Which lines stop here
  isTransfer?: boolean; // Transfer station (multiple lines meet)
}

// Line represents a user journey
interface Line {
  id: string;
  name: string;
  color: string;
  description: string;
  stations: string[]; // Station IDs in order
}

// Define all stations (screens) in the app
const stations: Station[] = [
  // Top Row - Landing & Auth Flow
  { id: 'landing', name: 'Landing Page', component: 'LandingPage.tsx', x: 150, y: 80, lines: ['blue', 'green', 'purple'], isTransfer: true },
  { id: 'login', name: 'Login', component: 'LoginScreen.tsx', x: 350, y: 80, lines: ['blue', 'purple'], isTransfer: true },
  { id: 'verify', name: 'Verify', component: 'VerifyScreen.tsx', x: 550, y: 80, lines: ['blue', 'purple'] },
  { id: 'register', name: 'Register', component: 'RegisterScreen.tsx', x: 750, y: 80, lines: ['blue', 'purple'] },
  { id: 'acceptance', name: 'Invitation', component: 'GiftGroupAcceptance.tsx', x: 950, y: 80, lines: ['purple'] },
  
  // Second Row - Gift Creation Steps 1-3
  { id: 'create-step1', name: 'Step 1: Date', component: 'CreateGiftGroupFlow (Step 1)', x: 150, y: 220, lines: ['blue', 'green'] },
  { id: 'create-step2', name: 'Step 2: Type', component: 'CreateGiftGroupFlow (Step 2)', x: 350, y: 220, lines: ['blue', 'green'] },
  { id: 'create-step3', name: 'Step 3: People', component: 'CreateGiftGroupFlow (Step 3)', x: 550, y: 220, lines: ['blue', 'green'] },
  
  // Third Row - Gift Creation Steps 4-6
  { id: 'create-step4', name: 'Step 4: Title', component: 'CreateGiftGroupFlow (Step 4)', x: 750, y: 220, lines: ['blue', 'green'] },
  { id: 'create-step5', name: 'Step 5: Amount', component: 'CreateGiftGroupFlow (Step 5)', x: 950, y: 220, lines: ['blue', 'green'] },
  { id: 'create-step6', name: 'Step 6: Review', component: 'CreateGiftGroupFlow (Step 6)', x: 1150, y: 220, lines: ['blue', 'green'] },
  
  // Central Hub - Dashboard
  { id: 'dashboard', name: 'Dashboard', component: 'Dashboard.tsx', x: 650, y: 380, lines: ['blue', 'green', 'purple', 'orange', 'yellow'], isTransfer: true },
  
  // Lower section - Gift Group Management
  { id: 'overview', name: 'Group Overview', component: 'GiftGroupOverview.tsx', x: 650, y: 540, lines: ['orange', 'yellow'], isTransfer: true },
  
  // Left branch - Contribution Flow
  { id: 'contribution', name: 'Contribute', component: 'GiftGroupContribution.tsx', x: 350, y: 540, lines: ['orange'] },
  { id: 'paypal', name: 'PayPal', component: 'PayPalFunds.tsx', x: 350, y: 680, lines: ['orange'] },
  
  // Right branch - Gift Selection & Ranking
  { id: 'rankings', name: 'Rankings', component: 'GiftRanking.tsx', x: 950, y: 680, lines: ['yellow'] },
  { id: 'ai-suggestions', name: 'AI Gifts', component: 'AIGiftSuggestions.tsx', x: 750, y: 820, lines: ['yellow'] },
  { id: 'manual-suggestions', name: 'Manual Entry', component: 'GiftSuggestions.tsx', x: 1050, y: 820, lines: ['yellow'] },
];

// Define all lines (user journeys)
const lines: Line[] = [
  {
    id: 'blue',
    name: 'New User Gift Creation',
    color: '#3b82f6',
    description: 'Unregistered user creates a gift group, authenticates, and completes setup',
    stations: ['landing', 'create-step1', 'create-step2', 'create-step3', 'create-step4', 'create-step5', 'create-step6', 'login', 'verify', 'register', 'dashboard'],
  },
  {
    id: 'green',
    name: 'Registered User Gift Creation',
    color: '#22c55e',
    description: 'Returning user creates a gift group (already authenticated)',
    stations: ['landing', 'create-step1', 'create-step2', 'create-step3', 'create-step4', 'create-step5', 'create-step6', 'dashboard'],
  },
  {
    id: 'purple',
    name: 'Invitation Acceptance',
    color: '#a855f7',
    description: 'User receives email invite, authenticates if needed, and joins gift group',
    stations: ['acceptance', 'login', 'verify', 'register', 'dashboard'],
  },
  {
    id: 'orange',
    name: 'Contribution Flow',
    color: '#f97316',
    description: 'User contributes funds to a gift group via PayPal',
    stations: ['dashboard', 'overview', 'contribution', 'paypal'],
  },
  {
    id: 'yellow',
    name: 'Gift Selection & Ranking',
    color: '#eab308',
    description: 'User adds gift suggestions (AI or manual) and ranks them',
    stations: ['dashboard', 'overview', 'rankings', 'ai-suggestions', 'manual-suggestions'],
  },
];

interface FlowSubwayMapProps {
  onNavigate?: (screen: string) => void;
}

export function FlowSubwayMap({ onNavigate }: FlowSubwayMapProps) {
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [selectedLine, setSelectedLine] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0.75);
  
  const viewBox = "0 0 1400 950";
  
  const handleZoomIn = () => setZoom(Math.min(zoom + 0.1, 2));
  const handleZoomOut = () => setZoom(Math.max(zoom - 0.1, 0.3));
  const handleReset = () => setZoom(0.7);
  
  // Get line path between two stations
  const getLinePath = (from: Station, to: Station): string => {
    // For subway aesthetic, use orthogonal lines (horizontal + vertical)
    const midX = (from.x + to.x) / 2;
    
    // If stations are on same row (y coordinate close), use straight horizontal line
    if (Math.abs(from.y - to.y) < 50) {
      return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
    }
    
    // Otherwise use L-shaped path (horizontal then vertical or vice versa)
    if (from.x < to.x) {
      // Going right: horizontal then vertical
      return `M ${from.x} ${from.y} L ${midX} ${from.y} L ${midX} ${to.y} L ${to.x} ${to.y}`;
    } else {
      // Going left: vertical then horizontal
      return `M ${from.x} ${from.y} L ${from.x} ${midX} L ${to.x} ${midX} L ${to.x} ${to.y}`;
    }
  };
  
  // Check if station should be highlighted
  const isStationHighlighted = (station: Station): boolean => {
    if (selectedLine) {
      return station.lines.includes(selectedLine);
    }
    if (selectedStation) {
      return station.id === selectedStation.id;
    }
    return false;
  };
  
  // Check if line should be highlighted
  const isLineHighlighted = (lineId: string): boolean => {
    if (selectedLine) {
      return lineId === selectedLine;
    }
    if (selectedStation) {
      return selectedStation.lines.includes(lineId);
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-orange-50 p-4 sm:p-8">
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-4 mb-4">
            {onNavigate && (
              <Button
                onClick={() => onNavigate('landing')}
                variant="outline"
                size="sm"
              >
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            )}
          </div>
          <h1 className="text-emerald-700">BtGtR User Flow Subway Map</h1>
          <p className="text-muted-foreground">
            Interactive visualization of all user journeys through the platform
          </p>
        </div>

        {/* Legend */}
        <Card className="p-6">
          <h2 className="mb-4">Lines (User Journeys)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lines.map((line) => (
              <button
                key={line.id}
                onClick={() => setSelectedLine(selectedLine === line.id ? null : line.id)}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  isLineHighlighted(line.id)
                    ? 'border-current shadow-lg scale-105'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{ color: line.color }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: line.color }}
                  />
                  <span className="font-semibold">{line.name}</span>
                </div>
                <p className="text-sm text-gray-600">{line.description}</p>
                <Badge variant="outline" className="mt-2">
                  {stations.filter((s) => s.lines.includes(line.id)).length} stops
                </Badge>
              </button>
            ))}
          </div>
        </Card>

        {/* Map Controls */}
        <div className="flex justify-center gap-2">
          <Button onClick={handleZoomOut} variant="outline" size="sm">
            <ZoomOut className="w-4 h-4 mr-1" />
            Zoom Out
          </Button>
          <Button onClick={handleReset} variant="outline" size="sm">
            <Maximize2 className="w-4 h-4 mr-1" />
            Reset
          </Button>
          <Button onClick={handleZoomIn} variant="outline" size="sm">
            <ZoomIn className="w-4 h-4 mr-1" />
            Zoom In
          </Button>
        </div>

        {/* Subway Map */}
        <Card className="p-4 overflow-auto">
          <div className="min-w-[900px]">
            <svg
              viewBox={viewBox}
              className="w-full transition-transform duration-300"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
            >
              {/* Draw all line paths */}
              {lines.map((line) => {
                const lineStations = line.stations.map((id) =>
                  stations.find((s) => s.id === id)
                ).filter(Boolean) as Station[];

                return (
                  <g key={line.id}>
                    {lineStations.slice(0, -1).map((station, idx) => {
                      const nextStation = lineStations[idx + 1];
                      const path = getLinePath(station, nextStation);
                      
                      return (
                        <path
                          key={`${line.id}-${idx}`}
                          d={path}
                          stroke={line.color}
                          strokeWidth={isLineHighlighted(line.id) ? 6 : 4}
                          fill="none"
                          opacity={
                            selectedLine && !isLineHighlighted(line.id)
                              ? 0.2
                              : selectedStation && !isLineHighlighted(line.id)
                              ? 0.3
                              : 1
                          }
                          className="transition-all"
                        />
                      );
                    })}
                  </g>
                );
              })}

              {/* Draw all stations */}
              {stations.map((station) => {
                const highlighted = isStationHighlighted(station);
                const dimmed =
                  (selectedLine && !highlighted) ||
                  (selectedStation && !highlighted);

                return (
                  <g
                    key={station.id}
                    onClick={() =>
                      setSelectedStation(
                        selectedStation?.id === station.id ? null : station
                      )
                    }
                    className="cursor-pointer"
                  >
                    {/* Transfer station indicator (larger circle) */}
                    {station.isTransfer && (
                      <circle
                        cx={station.x}
                        cy={station.y}
                        r={20}
                        fill="white"
                        stroke="#333"
                        strokeWidth={3}
                        opacity={dimmed ? 0.3 : 1}
                        className="transition-all"
                      />
                    )}
                    
                    {/* Main station circle */}
                    <circle
                      cx={station.x}
                      cy={station.y}
                      r={station.isTransfer ? 12 : 10}
                      fill="white"
                      stroke={highlighted ? '#000' : '#666'}
                      strokeWidth={highlighted ? 3 : 2}
                      opacity={dimmed ? 0.3 : 1}
                      className="transition-all hover:r-14"
                    />

                    {/* Station name label */}
                    <text
                      x={station.x}
                      y={station.y - 25}
                      textAnchor="middle"
                      fontSize="12"
                      fontWeight={highlighted ? 'bold' : 'normal'}
                      fill={dimmed ? '#999' : '#333'}
                      className="transition-all pointer-events-none"
                    >
                      {station.name}
                    </text>
                    
                    {/* Transfer indicator */}
                    {station.isTransfer && (
                      <text
                        x={station.x}
                        y={station.y + 35}
                        textAnchor="middle"
                        fontSize="10"
                        fill={dimmed ? '#999' : '#666'}
                        className="transition-all pointer-events-none"
                      >
                        ⇄ Transfer
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        </Card>

        {/* Selected Station Info */}
        {selectedStation && (
          <Card className="p-6 border-2 border-blue-500 bg-blue-50">
            <div className="space-y-4">
              <div>
                <h2 className="text-blue-700 mb-1">{selectedStation.name}</h2>
                <p className="text-sm text-gray-600">
                  Component: <code className="bg-white px-2 py-1 rounded">{selectedStation.component}</code>
                </p>
              </div>
              
              <div>
                <p className="mb-2">
                  <strong>Used in {selectedStation.lines.length} flow{selectedStation.lines.length > 1 ? 's' : ''}:</strong>
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedStation.lines.map((lineId) => {
                    const line = lines.find((l) => l.id === lineId);
                    if (!line) return null;
                    return (
                      <Badge
                        key={lineId}
                        style={{ backgroundColor: line.color, color: 'white' }}
                      >
                        {line.name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
              
              {selectedStation.isTransfer && (
                <div className="bg-yellow-100 border border-yellow-300 rounded p-3">
                  <p className="text-sm">
                    <strong>🔄 Transfer Station:</strong> This is a key junction where multiple user journeys intersect.
                  </p>
                </div>
              )}
              
              <Button
                onClick={() => setSelectedStation(null)}
                variant="outline"
                size="sm"
              >
                Close
              </Button>
            </div>
          </Card>
        )}

        {/* Instructions */}
        <Card className="p-6 bg-gray-50">
          <h3 className="mb-3">How to Use This Map</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>
              <strong>Click a colored line card</strong> in the legend to highlight that user journey
            </li>
            <li>
              <strong>Click a station (circle)</strong> on the map to see details about that screen
            </li>
            <li>
              <strong>Transfer stations</strong> (marked with ⇄) are where multiple user flows intersect
            </li>
            <li>
              <strong>Use zoom controls</strong> to get a closer look at specific areas
            </li>
            <li>
              <strong>Each colored line</strong> represents a complete user journey from start to finish
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
